// ============================================================================
// Dienstplanung (Wochen-Roster) — Types
// ============================================================================

/** Rolle eines Mitarbeiters im Dienstplan */
export type StaffRole = 'arzt' | 'mfa' | 'empfang' | 'vertretung' | 'labor';

/** Konfiguration einer Planungs-Position (Zeile im Roster) */
export interface PositionConfig {
  id: string;
  label: string;                 // "Sprechstunde", "Labor", "Empfang"
  requiredRole?: StaffRole;      // Optional: bevorzugte Rolle
  color: {
    bg: string;
    text: string;
    border: string;
  };
  sortOrder: number;
  maxStaff: number;              // Max Mitarbeiter pro Tag in dieser Position (1–10)
}

/** Optionen beim Veröffentlichen eines Dienstplans */
export interface SchedulePublishOptions {
  sendEmail: boolean;
  recipients: Array<{ staffId: string; name: string; email: string }>;
  kwNumber: number;
  practiceName: string;
  slots: ShiftSlot[];
}

/** Einzelner Schicht-Block im Wochenplan */
export interface ShiftSlot {
  id: string;
  schedule_id: string;
  practice_id: string;
  staff_id: string;
  staff_name: string;
  staff_role: StaffRole;
  position_id: string;           // Referenz auf PositionConfig.id
  start_date: string;            // ISO date "2026-03-04"
  end_date: string;              // ISO date "2026-03-08" (mehrtägig möglich)
  start_time?: string;           // "08:00" (optional für Halbtags)
  end_time?: string;             // "13:00"
  is_override: boolean;
  is_open?: boolean;
  note?: string;
  created_at: string;
  updated_at: string;
}

/** Wochenplan-Container */
export interface WeekSchedule {
  id: string;
  practice_id: string;
  week_start: string;            // ISO date (Montag der Woche)
  status: 'draft' | 'published';
  slots: ShiftSlot[];
  created_by: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
}

/** Mitarbeiter im Dienstplan (Sidebar) */
export interface ScheduleStaffMember {
  id: string;
  name: string;
  role: StaffRole;
  color: string;                 // Tailwind color class
  avatar_url?: string;
  is_available: boolean;
  absence_reason?: string;       // "Urlaub", "Krank"
}

// ============================================================================
// Konstanten
// ============================================================================

/** Farben pro Rolle (Fresha/Square-inspired) */
export const ROSTER_COLORS: Record<StaffRole, { bg: string; text: string; border: string; bgLight: string }> = {
  arzt:       { bg: 'bg-indigo-100',  text: 'text-indigo-700',  border: 'border-indigo-200',  bgLight: 'bg-indigo-50' },
  mfa:        { bg: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-blue-200',    bgLight: 'bg-blue-50' },
  empfang:    { bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-200',   bgLight: 'bg-amber-50' },
  labor:      { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', bgLight: 'bg-emerald-50' },
  vertretung: { bg: 'bg-neutral-100', text: 'text-neutral-600', border: 'border-neutral-200', bgLight: 'bg-neutral-50' },
};

/** Standard-Positionen für den Dienstplan */
export const DEFAULT_POSITIONS: PositionConfig[] = [
  {
    id: 'sprechstunde',
    label: 'Sprechstunde',
    requiredRole: 'arzt',
    color: ROSTER_COLORS.arzt,
    sortOrder: 1,
    maxStaff: 2,
  },
  {
    id: 'labor',
    label: 'Labor',
    requiredRole: 'labor',
    color: ROSTER_COLORS.labor,
    sortOrder: 2,
    maxStaff: 1,
  },
  {
    id: 'empfang',
    label: 'Empfang',
    requiredRole: 'empfang',
    color: ROSTER_COLORS.empfang,
    sortOrder: 3,
    maxStaff: 2,
  },
  {
    id: 'vertretung',
    label: 'Vertretung',
    requiredRole: 'vertretung',
    color: ROSTER_COLORS.vertretung,
    sortOrder: 4,
    maxStaff: 1,
  },
];

/** Tage der Arbeitswoche */
export const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr'] as const;
export const WEEKDAYS_FULL = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'] as const;

// ============================================================================
// MVP Parity Types (Marktführer-Standard)
// ============================================================================

/** Verfügbarkeitsstatus eines Mitarbeiters */
export type AvailabilityStatus = 'available' | 'preferred' | 'unavailable' | 'vacation' | 'sick';

export const AVAILABILITY_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Verfügbar',
  preferred: 'Bevorzugt',
  unavailable: 'Nicht verfügbar',
  vacation: 'Urlaub',
  sick: 'Krank',
};

export const AVAILABILITY_COLORS: Record<AvailabilityStatus, { bg: string; text: string; dot: string }> = {
  available: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  preferred: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  unavailable: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  vacation: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  sick: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
};

/** Verfügbarkeit eines Mitarbeiters pro Tag */
export interface StaffAvailability {
  id: string;
  practice_id: string;
  staff_id: string;
  staff_name: string;
  date: string;
  availability: AvailabilityStatus;
  preferred_shift?: 'morning' | 'afternoon' | 'full_day' | null;
  note?: string;
  created_at: string;
  updated_at: string;
}

/** Schichttausch-Anfrage */
export interface ScheduleSwapRequest {
  id: string;
  practice_id: string;
  requester_id: string;
  requester_name: string;
  requester_slot_id: string;
  target_staff_id?: string;
  target_staff_name?: string;
  target_slot_id?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'approved';
  reason?: string;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
  // Joined
  requester_slot?: ShiftSlot;
  target_slot?: ShiftSlot;
}

/** ArbZG Compliance-Verletzung */
export interface ArbZGViolation {
  violation_type: 'max_weekly_hours' | 'max_daily_hours' | 'max_consecutive_days' | 'overtime_warning';
  severity: 'error' | 'warning' | 'info';
  message: string;
  affected_date: string;
}

/** Soll/Ist Stunden-Zusammenfassung */
export interface HoursSummaryRow {
  staff_id: string;
  staff_name: string;
  staff_role: StaffRole;
  planned_hours: number;
  contract_hours: number;
  diff_hours: number;
  shift_count: number;
}

/** Vertragsstunden eines Mitarbeiters */
export interface StaffContractHours {
  id: string;
  practice_id: string;
  staff_id: string;
  staff_name: string;
  weekly_hours: number;
  valid_from: string;
  valid_until?: string;
}

/** Explizite Qualifikation eines Mitarbeiters für eine Position */
export type StaffPositionQualificationStatus = 'qualified' | 'fallback_allowed' | 'not_qualified';

export interface StaffPositionQualification {
  id: string;
  practice_id: string;
  staff_id: string;
  position_id: string;
  qualification_status: StaffPositionQualificationStatus;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

/** Herkunft der Smart-Plan-Kompatibilität */
export type SuggestionQualificationStatus =
  | 'qualified'
  | 'fallback_allowed'
  | 'role_match'
  | 'role_fallback';

/** Persistente Smart-Plan-Events als Lernbasis */
export type ScheduleSuggestionEventType =
  | 'suggested'
  | 'accepted'
  | 'rejected'
  | 'overridden'
  | 'expired';

export interface ScheduleSuggestionEvent {
  id: string;
  practice_id: string;
  schedule_id?: string | null;
  week_start: string;
  suggestion_run_id: string;
  suggestion_temp_id: string;
  staff_id: string;
  staff_role: StaffRole;
  position_id: string;
  target_date: string;
  target_open_slot_id?: string | null;
  suggested_score: number;
  suggested_confidence?: 'high' | 'medium' | 'low' | null;
  engine_version: string;
  event_type: ScheduleSuggestionEventType;
  reason_code?: string | null;
  reason_text?: string | null;
  metadata: Record<string, unknown>;
  actor_user_id?: string | null;
  event_at: string;
}
